// ============================================
// Zen-AI-Pentest Platform - New Scan Page
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Play, Loader2, Target, Zap, Shield, Globe, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { scanApi } from '@/services/api';
import { toast } from 'sonner';
import type { ScanConfig, ScanIntensity } from '@/types';

// ============================================
// Scan Types Configuration
// ============================================
const scanTypes = [
  {
    id: 'port_scan',
    name: 'Port Scan',
    description: 'Discover open ports and services',
    icon: Network,
    color: 'bg-blue-500',
  },
  {
    id: 'vulnerability_scan',
    name: 'Vulnerability Scan',
    description: 'Identify security vulnerabilities',
    icon: Shield,
    color: 'bg-red-500',
  },
  {
    id: 'web_scan',
    name: 'Web Application Scan',
    description: 'Test web applications for security issues',
    icon: Globe,
    color: 'bg-green-500',
  },
  {
    id: 'network_scan',
    name: 'Network Discovery',
    description: 'Map network topology and devices',
    icon: Network,
    color: 'bg-purple-500',
  },
  {
    id: 'compliance_scan',
    name: 'Compliance Scan',
    description: 'Check compliance against standards',
    icon: Target,
    color: 'bg-orange-500',
  },
  {
    id: 'ai_assisted_scan',
    name: 'AI-Assisted Scan',
    description: 'AI-powered deep security analysis',
    icon: Zap,
    color: 'bg-indigo-500',
  },
];

// ============================================
// Intensity Options
// ============================================
const intensityOptions: { value: ScanIntensity; label: string; description: string }[] = [
  {
    value: 'passive',
    label: 'Passive',
    description: 'Non-intrusive reconnaissance only',
  },
  {
    value: 'normal',
    label: 'Normal',
    description: 'Balanced speed and thoroughness',
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    description: 'Maximum coverage, may be intrusive',
  },
];

// ============================================
// Main New Scan Page
// ============================================
export function NewScanPage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    target: '',
    type: 'port_scan',
    description: '',
    config: {
      intensity: 'normal' as ScanIntensity,
      timeout: 3600,
      threads: 10,
      ports: [80, 443, 22, 21, 25, 53, 3306, 5432, 8080, 8443],
      options: {
        serviceDetection: true,
        osDetection: false,
        versionDetection: true,
        scriptScan: false,
        traceroute: false,
      },
    } as ScanConfig,
  });

  const createMutation = useMutation({
    mutationFn: scanApi.create,
    onSuccess: (data) => {
      toast.success('Scan created successfully');
      navigate(`/scans/${data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create scan');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.target.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    createMutation.mutate({
      name: formData.name,
      target: formData.target,
      type: formData.type,
      config: formData.config,
    });
  };

  const selectedType = scanTypes.find((t) => t.id === formData.type);
  const TypeIcon = selectedType?.icon || Network;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/scans')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New Scan</h1>
          <p className="text-muted-foreground">
            Configure and launch a new security scan
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Enter the basic details for your scan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Scan Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., Production Network Scan"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target">
                    Target <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="target"
                    placeholder="e.g., 192.168.1.0/24 or example.com"
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter an IP address, IP range (CIDR), hostname, or domain
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Optional description of this scan..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Scan Type Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Scan Type</CardTitle>
                <CardDescription>
                  Select the type of scan you want to perform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {scanTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.type === type.id;

                    return (
                      <div
                        key={type.id}
                        onClick={() => setFormData({ ...formData, type: type.id })}
                        className={`cursor-pointer rounded-lg border p-4 transition-all hover:border-primary ${
                          isSelected ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${type.color} bg-opacity-10 flex items-center justify-center`}>
                            <Icon className={`w-5 h-5 ${type.color.replace('bg-', 'text-')}`} />
                          </div>
                          <div>
                            <h4 className="font-medium">{type.name}</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Advanced Options */}
            <Card>
              <CardHeader>
                <CardTitle>Advanced Options</CardTitle>
                <CardDescription>
                  Configure advanced scan parameters
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="general">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="ports">Ports</TabsTrigger>
                    <TabsTrigger value="plugins">Plugins</TabsTrigger>
                  </TabsList>

                  <TabsContent value="general" className="space-y-4 pt-4">
                    {/* Intensity */}
                    <div className="space-y-2">
                      <Label>Scan Intensity</Label>
                      <div className="grid gap-3">
                        {intensityOptions.map((option) => (
                          <div
                            key={option.value}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                config: { ...formData.config, intensity: option.value },
                              })
                            }
                            className={`cursor-pointer rounded-lg border p-3 transition-all hover:border-primary ${
                              formData.config.intensity === option.value
                                ? 'border-primary bg-primary/5'
                                : 'border-border'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium">{option.label}</span>
                                <p className="text-xs text-muted-foreground">
                                  {option.description}
                                </p>
                              </div>
                              {formData.config.intensity === option.value && (
                                <Badge variant="default">Selected</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Timeout */}
                    <div className="space-y-2">
                      <Label>Timeout (seconds)</Label>
                      <Slider
                        value={[formData.config.timeout]}
                        onValueChange={([value]) =>
                          setFormData({
                            ...formData,
                            config: { ...formData.config, timeout: value },
                          })
                        }
                        min={300}
                        max={7200}
                        step={300}
                      />
                      <p className="text-xs text-muted-foreground">
                        {Math.floor(formData.config.timeout / 60)} minutes
                      </p>
                    </div>

                    {/* Threads */}
                    <div className="space-y-2">
                      <Label>Concurrent Threads</Label>
                      <Slider
                        value={[formData.config.threads]}
                        onValueChange={([value]) =>
                          setFormData({
                            ...formData,
                            config: { ...formData.config, threads: value },
                          })
                        }
                        min={1}
                        max={50}
                        step={1}
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.config.threads} threads
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="ports" className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Port Selection</Label>
                      <Select
                        value="common"
                        onValueChange={(value) => {
                          const portSets: Record<string, number[]> = {
                            common: [80, 443, 22, 21, 25, 53, 3306, 5432, 8080, 8443],
                            full: Array.from({ length: 65535 }, (_, i) => i + 1),
                            web: [80, 443, 8080, 8443, 3000, 5000, 8000, 9000],
                            database: [3306, 5432, 27017, 6379, 9200, 1433, 1521],
                          };
                          setFormData({
                            ...formData,
                            config: { ...formData.config, ports: portSets[value] },
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select port range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="common">Common Ports (Top 10)</SelectItem>
                          <SelectItem value="full">All Ports (1-65535)</SelectItem>
                          <SelectItem value="web">Web Services</SelectItem>
                          <SelectItem value="database">Database Services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Custom Ports</Label>
                      <Input
                        placeholder="e.g., 8080, 9000, 10000"
                        onChange={(e) => {
                          const ports = e.target.value
                            .split(',')
                            .map((p) => parseInt(p.trim()))
                            .filter((p) => !isNaN(p));
                          setFormData({
                            ...formData,
                            config: { ...formData.config, ports },
                          });
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        Comma-separated list of ports
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {formData.config.ports?.slice(0, 20).map((port) => (
                        <Badge key={port} variant="secondary">
                          {port}
                        </Badge>
                      ))}
                      {formData.config.ports && formData.config.ports.length > 20 && (
                        <Badge variant="outline">
                          +{formData.config.ports.length - 20} more
                        </Badge>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="plugins" className="space-y-4 pt-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium">Service Detection</Label>
                          <p className="text-xs text-muted-foreground">
                            Detect services running on open ports
                          </p>
                        </div>
                        <Switch
                          checked={formData.config.options?.serviceDetection}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              config: {
                                ...formData.config,
                                options: {
                                  ...formData.config.options,
                                  serviceDetection: checked,
                                },
                              },
                            })
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium">OS Detection</Label>
                          <p className="text-xs text-muted-foreground">
                            Attempt to identify operating systems
                          </p>
                        </div>
                        <Switch
                          checked={formData.config.options?.osDetection}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              config: {
                                ...formData.config,
                                options: {
                                  ...formData.config.options,
                                  osDetection: checked,
                                },
                              },
                            })
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium">Version Detection</Label>
                          <p className="text-xs text-muted-foreground">
                            Detect service versions
                          </p>
                        </div>
                        <Switch
                          checked={formData.config.options?.versionDetection}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              config: {
                                ...formData.config,
                                options: {
                                  ...formData.config.options,
                                  versionDetection: checked,
                                },
                              },
                            })
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium">Script Scan</Label>
                          <p className="text-xs text-muted-foreground">
                            Run vulnerability detection scripts
                          </p>
                        </div>
                        <Switch
                          checked={formData.config.options?.scriptScan}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              config: {
                                ...formData.config,
                                options: {
                                  ...formData.config.options,
                                  scriptScan: checked,
                                },
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Scan Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${selectedType?.color} bg-opacity-10 flex items-center justify-center`}>
                    <TypeIcon className={`w-5 h-5 ${selectedType?.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div>
                    <p className="font-medium">{selectedType?.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedType?.description}</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Intensity</span>
                    <span className="font-medium capitalize">{formData.config.intensity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Timeout</span>
                    <span className="font-medium">{Math.floor(formData.config.timeout / 60)} min</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Threads</span>
                    <span className="font-medium">{formData.config.threads}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ports</span>
                    <span className="font-medium">{formData.config.ports?.length || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="pt-6 space-y-3">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Start Scan
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/scans')}
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
